import { DatePicker, Select, SelectProps } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { IRestTotalReq } from '@api/services/rKeeperApi/types';
import { geReportAbc } from '@api/thunks/reports';
import { getRestaurants } from '@api/thunks/rKeeper';
import { Icons } from '@assets';
import { AppDispatch } from '@config/store';
import { useApplication } from '@/context/ApplicationContext';
import { authInfoSelect } from '@entities/auth';
import { rKeeperSelect } from '@entities/rKeeper';
import { colors } from '@styles';
import { flexMixin, textMixin } from '@styles/mixins';
import { SelectOption } from '@types';
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT } from '@utils/const';
import { isNotEmptyArray } from '@utils/functions';
import { BarChartAbc } from './components/reports/BarChartAbc';
import TableReportAbc from './components/reports/TableReportAbc';
const { RangePicker } = DatePicker;

const ReportABCPage: FC = () => {
    const { dateRange, setDateRange } = useApplication();
    const { shop } = useSelector(authInfoSelect);
    const { restaurants } = useSelector(rKeeperSelect);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    const from = useMemo(() => {
        return dateRange && dateRange.from
            ? dateRange.from
            : moment().add(-1, 'month').utc().format(API_DATE_FORMAT).valueOf();
    }, [dateRange]);
    const to = useMemo(
        () =>
            dateRange && dateRange.to
                ? dateRange.to
                : moment().utc().format(API_DATE_FORMAT).valueOf().valueOf(),
        [dateRange]
    );

    const dispatch = useDispatch<AppDispatch>();

    const options: SelectProps['options'] = useMemo(
        () => [
            { label: 'Выбрать все', value: '0' },
            ...(restaurants?.map(item => ({
                label: item.name,
                value: item.id.toString(),
            })) || []),
        ],
        [restaurants]
    );

    useEffect(() => {
        if (isNotEmptyArray(restaurants)) {
            const allRestaurantValues = restaurants
                .map(item => item.id?.toString())
                .filter((id): id is string => id !== undefined && id !== null);

            setSelectedValues(allRestaurantValues);
        }
    }, [restaurants]);

    const handleChange = (values: string[]) => {
        if (values.includes('0')) {
            if (!selectedValues.includes('0')) {
                const allValues = options
                    .filter(
                        (opt): opt is SelectOption =>
                            !!opt.value && opt.value !== '0'
                    )
                    .map(opt => opt.value);
                setSelectedValues([...allValues, '0']);
            } else {
                setSelectedValues([]);
            }
        } else {
            const filteredValues = values.filter(value => value !== '0');
            setSelectedValues(filteredValues);
        }
    };

    useEffect(() => {
        if (shop) {
            dispatch(getRestaurants(shop.id));
        }
    }, [shop, dispatch]);

    useEffect(() => {
        if (shop) {
            const params: IRestTotalReq = {
                from: moment.utc(from).format(API_DATE_FORMAT),
                to: moment.utc(to).format(API_DATE_FORMAT),
            };
            if (selectedValues[0]) {
                params.restaurant = selectedValues.join(',');
            }

            dispatch(geReportAbc({ shop_id: shop.id, params }));
        }
    }, [shop, dispatch, from, to, selectedValues]);

    const onDateChange = (dates: any, dateStrings: [string, string]) => {
        setDateRange({
            from: dayjs(dateStrings[0], DISPLAY_DATE_FORMAT)
                .format(API_DATE_FORMAT)
                .valueOf(),
            to: dayjs(dateStrings[1], DISPLAY_DATE_FORMAT)
                .format(API_DATE_FORMAT)
                .valueOf(),
        });
    };

    return (
        <Wrapper>
            <Header>
                <StyledHeadTitle>АВС-анализ</StyledHeadTitle>
            </Header>
            <InfoWrapper>
                <WrapSelect>
                    <LabelWrapper>Выбор ресторана:</LabelWrapper>
                    <Select
                        mode="multiple"
                        allowClear
                        style={{ minWidth: '240px' }}
                        maxTagCount={7}
                        placeholder="Выбрать рестораны"
                        value={selectedValues}
                        onChange={handleChange}
                        options={options}
                    />
                </WrapSelect>
                <StyledCaledarWrapper>
                    <StyledRangePicker
                        suffixIcon={
                            <img
                                src={Icons.IconMainCalendar}
                                alt="icon calendar"
                            />
                        }
                        onChange={onDateChange}
                        value={[
                            dayjs(from, API_DATE_FORMAT),
                            dayjs(to, API_DATE_FORMAT),
                        ]}
                        format={DISPLAY_DATE_FORMAT}
                    />
                </StyledCaledarWrapper>
            </InfoWrapper>

            <div>
                <ChartsWrapper>
                    <BarChartAbc />
                </ChartsWrapper>
            </div>
            <TableReportAbc />
        </Wrapper>
    );
};

export default ReportABCPage;

const Wrapper = styled.div`
    min-width: 100%;
    height: 80%;
    background-color: ${colors.bgWhite};
    padding: 16px 0px 16px 0px;
`;
const InfoWrapper = styled.div`
    margin: 20px 0px;
    ${flexMixin('row', 'center', 'flex-end')};
    flex-wrap: wrap;
    gap: 5px;
`;

const LabelWrapper = styled.div`
    width: 150px;
`;
const WrapSelect = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    flex: 1;
    .ant-select-selection-item-content {
        height: 24px;
        line-height: 22px;
    }
`;

const Header = styled.div`
    width: 100%;
    ${flexMixin('row', 'space-between', 'center')};
    margin-bottom: 20px;
`;

const StyledRangePicker = styled(RangePicker)`
    width: 250px;
    color: ${colors.cleanWhite};
`;

const StyledHeadTitle = styled.label`
    ${textMixin({
        fontSize: '25px',
        fontWeight: 600,
    })};
    line-height: 28px;
    text-align: left;
    color: ${colors.grey};
`;

const StyledCaledarWrapper = styled.div`
    color: ${colors.cleanWhite};

    & svg {
        width: 20px;
        height: 20px;
    }
    .ant-picker {
        background-color: ${colors.primaryBg};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 28px;
        text-align: center;
        color: ${colors.cleanWhite};
    }

    .ant-picker .ant-picker-input,
    .ant-picker .ant-picker-range,
    .ant-picker .ant-picker-input-active .ant-picker-input-placeholder {
        display: flex;
        flex-direction: row-reverse;
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 28px;
        color: ${colors.cleanWhite};
    }

    .ant-picker .ant-picker-input .ant-picker-input-active {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker .ant-picker-input .ant-picker-input-placeholder {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker .ant-picker-input span.ant-picker-suffix,
    .ant-picker.ant-picker-range span.ant-picker-suffix {
        order: -1;
        margin-left: 0;
        margin-right: 10px;
        color: ${colors.cleanWhite};
    }

    .ant-picker.ant-picker-range.ant-picker-range-separator {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker-range-separator > span {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker-clear {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker .ant-picker-clear {
        position: absolute;
        right: 217px;
    }
`;

const ChartsWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 400px;
    max-height: 100vh;
    margin-bottom: 20px;
    padding: 30px;
    background-color: ${colors.cleanWhite};
    border-radius: 5px;

    .chart-content-wrapper {
        flex: 1;
        min-height: 300px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .chart-container {
        position: relative;
        flex: 1;
        min-height: 200px;
    }

    .legend-container {
        flex-shrink: 0;
        margin-bottom: 20px;
    }
`;
